import { useFeedback, useAuth } from "@taunggyi-plus/auth";
import { useEffect, useState } from "react";
import { Card, ServerPagination, RecordFormDialog } from "@taunggyi-plus/design-system";
type Reviews = {
  total: number;
  average: number | null;
  items: { id: string; rating: number; comment: string }[];
};
type CompletedOrder = {
  id: string;
  org: string;
  service: string;
  label: string;
  finished: boolean;
};
export function ProviderSaveButton({ org }: { org: string }) {
  const { client, user } = useAuth();
  const [saved, setSaved] = useState(false),
    [busy, setBusy] = useState(false);
  const [, setMessage] = useFeedback();
  useEffect(() => {
    let active = true;
    if (client && user)
      void client
        .from("customer_favourites")
        .select("organization_id")
        .eq("organization_id", org)
        .then((r) => {
          if (active && !r.error) setSaved(Boolean(r.data?.length));
        });
    return () => {
      active = false;
    };
  }, [client, user, org]);
  if (!user) return null;
  return (
    <button
      type="button"
      className="icon-button provider-reputation__save"
      aria-label={saved ? "Remove from favourites" : "Save to favourites"}
      title={saved ? "Remove from favourites" : "Save to favourites"}
      aria-pressed={saved}
      disabled={busy}
      onClick={async () => {
        if (!client) return;
        setBusy(true);
        const r = await client.rpc("set_provider_favourite", { org, favourite: !saved });
        if (r.error) setMessage(r.error.message);
        else setSaved(!saved);
        setBusy(false);
      }}
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill={saved ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.8"
        aria-hidden="true"
      >
        <path d="M6 4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17l-6-4-6 4z" />
      </svg>
    </button>
  );
}
export function ProviderReputation({
  org,
  driverId,
  hideSave = false,
}: {
  org: string;
  driverId?: string;
  hideSave?: boolean;
}) {
  const { client, user } = useAuth();
  const [page, setPage] = useState(0),
    [version, setVersion] = useState(0),
    [reviews, setReviews] = useState<Reviews>({ total: 0, average: null, items: [] }),
    [orders, setOrders] = useState<CompletedOrder[]>([]),
    [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    if (!client || !user) return;
    let active = true;
    void Promise.all([
      driverId
        ? client.rpc("taxi_driver_reviews", { driver_id: driverId, page })
        : client.rpc("provider_review_page", { org, page }),
      client.rpc("my_service_orders"),
      driverId
        ? client
            .from("taxi_orders")
            .select("id")
            .eq("driver_id", driverId)
            .eq("customer_user_id", user.id)
        : Promise.resolve({ data: null, error: null }),
    ]).then(([r, o, t]) => {
      if (!active) return;
      if (r.error || o.error || t.error) {
        setError(
          r.error?.message ?? o.error?.message ?? t.error?.message ?? "Could not load reviews.",
        );
        return;
      }
      const next = r.data as Reviews;
      setReviews((old) =>
        page === 0
          ? next
          : {
              ...next,
              items: [
                ...old.items.filter((i) => !next.items.some((n) => n.id === i.id)),
                ...next.items,
              ],
            },
      );
      setOrders(
        ((o.data ?? []) as CompletedOrder[]).filter(
          (i) =>
            i.org === org &&
            i.finished &&
            (!driverId ||
              (i.service === "taxi" && (t.data as { id: string }[]).some((t) => t.id === i.id))),
        ),
      );
      setError("");
    });
    return () => {
      active = false;
    };
  }, [client, user, org, driverId, page, version]);
  if (!user) return null;
  return (
    <Card className={`provider-reputation ${hideSave ? "provider-reputation--reviews-only" : ""}`}>
      <div className="provider-reviews-heading">
        <div>
          <h3>Customer reviews</h3>
          <p>
            {reviews.total
              ? `${reviews.average} / 5 · ${reviews.total} verified reviews`
              : "No reviews yet"}
          </p>
        </div>
        {orders.length > 0 && (
          <button className="button button--secondary" onClick={() => setOpen(true)}>
            Write a review
          </button>
        )}
      </div>
      {!orders.length && !reviews.total && (
        <p className="provider-review-hint">
          {driverId
            ? "Complete a ride to share your experience."
            : "Complete a service to share your experience."}
        </p>
      )}
      {reviews.items.map((r) => (
        <article className="provider-review-row" key={r.id}>
          <span className="provider-review-stars" aria-label={`${r.rating} out of 5`}>
            {"★".repeat(r.rating)}
            {"☆".repeat(5 - r.rating)}
          </span>
          <p>{r.comment}</p>
          <small>Verified completed service</small>
        </article>
      ))}
      {reviews.total > 10 && (
        <ServerPagination page={page} total={reviews.total} onPage={setPage} />
      )}
      {!hideSave && <ProviderSaveButton org={org} />}
      {error && <p role="alert">{error}</p>}
      <RecordFormDialog
        title="Write a review"
        open={open}
        hideTrigger
        onClose={() => setOpen(false)}
      >
        <form
          className="form-grid"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!client) return;
            const f = new FormData(e.currentTarget);
            const order = orders.find((o) => o.id === f.get("order"));
            if (!order) return;
            setBusy(true);
            const r = await client.rpc("review_completed_service", {
              service: order.service,
              order_id: order.id,
              rating: Number(f.get("rating")),
              comment: String(f.get("comment")).trim(),
            });
            setBusy(false);
            if (r.error) setError(r.error.message);
            else {
              setOpen(false);
              setPage(0);
              setVersion((v) => v + 1);
            }
          }}
        >
          <label className="field">
            <span>Completed service</span>
            <select name="order" required>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label} · #{o.id.slice(0, 8)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Rating</span>
            <select name="rating" defaultValue="5">
              {[5, 4, 3, 2, 1].map((n) => (
                <option key={n} value={n}>
                  {n} / 5
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Your review</span>
            <textarea
              name="comment"
              required
              minLength={3}
              maxLength={1000}
              rows={4}
              placeholder="How was your experience?"
            />
          </label>
          {error && <p role="alert">{error}</p>}
          <button className="button" disabled={busy}>
            {busy ? "Submitting…" : "Submit review"}
          </button>
        </form>
      </RecordFormDialog>
    </Card>
  );
}
